import { amorphicStatic, supertypeClass, Supertype } from '@haventech/amorphic';
import jwt from 'jsonwebtoken';
import rp from 'request-promise-native';
import { Policy } from '../../../common/js/customer/Policy';
import { PolicyDoc } from '../../../common/js/customer/PolicyDoc';
import { CdmService, AgencyCorpsRelationship } from '../../../common/js/services/CdmService';
import { CoverpathUtils } from './CoverpathUtils';
import { CoverpathAnnuity } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { Person } from '../../../common/js/customer/Person';
import { ExistingPolicy } from '../../../common/js/customer/ExistingPolicy';
import { FlatExtra } from '../../../common/js/customer/FlatExtra';
import { FlatExtra as uwFlatExtras } from '@hit/underwriting-api-client';
import { CollectionItemChange, PolicyChangeHistory, ValueType } from '../../../common/js/customer/PolicyChangeHistory';
import { PolicyAmendmentProperty } from '../../../common/js/static/PolicyAmendmentProperty';
import {
  POLICY_CHANGE_HISTORY_STATUS,
  POLICY_CHANGE_EDITOR,
} from '../../../common/js/static/PolicyChangeHistoryStatus';
import { Utils } from '../../../common/js/Utils';
import jsonStringifyDeterministic from 'json-stringify-deterministic';
import {
  TermLifeAmendmentForm,
  WholeLifeAmendmentForm,
} from '../../../coverpath-agent/public/js/components/amendment/AmendmentFormInterface';
import { ElasticSchema } from '@hit/hit-schemas';
import { RabbitmqPublisher } from '../../../common/js/rabbitmqPublisher/RabbitmqPublisher';
import { CoverpathAnnuityApplication } from '../../classes/coverpath_subclasses/CoverpathAnnuityApplication';
import { getDefaultAddressForParty } from './AnnuityPolicyAdminAndProductUtilities';
import { Agent } from '../../../common/js/customer/Agent';
import { AnnuityMarket } from '../interfaces/CoverpathTypes';
import { SecurityUtils } from './SecurityUtils';
import { EligibleToConvertToExistingWLProductTypes } from '../../../common/js/static/Assumptions';
import { LowFaceWholeLife, Product } from '../../../common/js/static/Product';
import { LoggerSingleton } from '../../../common/js/logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../../../common/js/logger/UILoggerSingleton';

const SOCIETY_1851_THRESHOLD = 50;

const SWAP_FIELDS_FROM_AMENDMENT = [
  'effectiveDate',
  'tLICEffectiveDate',
  'faceAmount',
  'maxFace',
  'maxAnnualPremium',
  'paymentFrequency',
  'hasEcp',
  'product',
  'productType',
  'selectedQuote',
  'term',
  'waiver',
  'wholeAPL',
  'loanInterestType',
  'dividendOption',
  'premiumBreakdown.base',
  'premiumBreakdown.waiver',
  'tLICPremiumBreakdown.base',
  'tLICPremiumBreakdown.waiver',
  'initialAcceleratedRateClass',
  'acceleratedRateClass',
  'tLICRateClass',
  'tLICAlgoRateClass',
  'finalAlgoRateClass',
  'finalManualRateClass',
  'finalRateClass',
  'type',
];

@supertypeClass({ toClient: false, toServer: false })
export class CoverpathUtilsBackEnd extends Supertype {
  /**
   *
   * @param changes object from framework
   * Confirm there is at least one property being changed
   * changes object sometimes did not contain any properties that were changed
   * so this function confirms some change actually occurred
   */
  static confirmChangesOccurred(changes): boolean {
    const changesArrayOfObjects: Array<any> = Object.values(changes);
    for (const objectArray of changesArrayOfObjects) {
      if (Array.isArray(objectArray)) {
        for (const object of objectArray) {
          if (object.properties && object.properties.length) {
            // if any of the objects have at least one property change, return true
            return true;
          }
        }
      }
    }

    // none of the objects have at least one property change
    return false;
  }

  static get baseByPolicyNumberQuery() {
    return `SELECT base FROM premium_breakdown pb
            INNER JOIN policy p on pb."_id" = p."premiumBreakdown_id"
            WHERE p."policyNumber" = ?`;
  }

  static async getBaseByPolicyNumberQuery(policyNumber: string): Promise<number> {
    const base = await Policy.getKnex().client.raw(this.baseByPolicyNumberQuery, [policyNumber]);
    return base.rows[0];
  }

  static async getInsuredDocsToBeSigned(policy) {
    const docQuery = {
      $and: [
        { policyId: policy._id },
        { appType: 'Amendment' },
        { isActive: 'true' },
        {
          $or: [
            { name: CoverpathUtils.getAmendmentFormNameByState(policy.getApplicationState()) },
            { name: CoverpathUtils.getInsurabilityFormNameByState(policy.getApplicationState()) },
          ],
        },
      ],
    };

    return PolicyDoc.persistorFetchByQuery(docQuery, {
      transient: true,
    });
  }

  static async getDocsToBeViewed(policy) {
    const docQuery = {
      $and: [{ policyId: policy._id }, { appType: 'AppPdf' }, { isActive: 'true' }, { name: 'ConsentedApplication' }],
    };

    return PolicyDoc.persistorFetchByQuery(docQuery, {
      transient: true,
    });
  }

  /**
   * Returns a single PolicyDoc for the given policy and appType, or undefined if there is none
   */
  static async fetchSinglePolicyDoc(policy: Policy, appType: string): Promise<PolicyDoc | undefined> {
    const query = {
      policyId: policy._id,
      appType: appType,
      isActive: 'true',
    };

    const policyDocs = await PolicyDoc.persistorFetchByQuery(query, {
      transient: true,
    });

    if (policyDocs.length === 0) {
      return undefined;
    } else if (policyDocs.length === 1) {
      return policyDocs[0];
    } else {
      const error = {
        isHumanRelated: false,
        data: {
          ...query,
          policyNumber: policy.policyNumber,
          matches: policyDocs.length,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'CoverpathUtilsBackEnd',
          function: 'fetchSinglePolicyDoc',
          category: UICategory.request,
          error: error,
        },
        'Policy has more than one document of specified type'
      );

      throw new Error('Policy has more than one document');
    }
  }

  static async fetchPolicyDocById(policyDocId: string): Promise<PolicyDoc> {
    const query = {
      _id: policyDocId,
    };
    const policyDocs = await PolicyDoc.persistorFetchByQuery(query, {
      transient: true,
    });
    if (policyDocs.length === 1) {
      return policyDocs[0];
    } else {
      UILoggerSingleton.getInstance().error(
        {
          module: 'CoverpathUtilsBackEnd',
          activity: 'fetchPolicyDocById',
          category: UICategory.request,
          data: {
            ...query,
            _id: policyDocId,
            matches: policyDocs.length,
          },
        },
        'Policy has more than one document of specified type'
      );

      throw new Error('Policy has more than one document');
    }
  }

  static async fetchPolicyDocs(policy: Policy): Promise<Array<PolicyDoc>> {
    const query = {
      policyId: policy._id,
      isActive: 'true',
    };

    const policyDocs = await PolicyDoc.persistorFetchByQuery(query, {
      transient: true,
    });
    return policyDocs;
  }

  static async fetchAnnuityByQuery(query: { [key: string]: string }): Promise<CoverpathAnnuity> {
    const annuities = await CoverpathAnnuity.persistorFetchByQuery(query, {
      fetch: { application: true },
    });

    if (annuities.length !== 1) {
      const error = {
        isHumanRelated: false,
        data: {
          query,
          matches: annuities.length,
        },
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'AnnuityServerSideController',
          function: 'fetchAnnuityByQuery',
          category: UICategory.request,
          error: error,
        },
        'Did not find a single annuity for the specified query'
      );

      throw new Error('Did not find a single annuity for the specified query');
    }

    const annuity = annuities[0];

    return annuity;
  }

  static getPdfStreamUrl() {
    return amorphicStatic.config.nconf.get('pdfstreamUrl');
  }

  static getPdfStreamHeaders() {
    const token = Utils.getAuthTokenForPdfstream();
    const headers = {
      'Content-Type': 'application/json',
      'authorization': 'Bearer ' + token,
      ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
    };
    return headers;
  }

  /**
   * Merges PDFs using PDFStream
   * @param files array of encoded base64 files
   * @param return Base64 Encoded merged PDF
   */
  static async mergePDFs(files: string[]): Promise<string> {
    const PDFSTREAM_URL = CoverpathUtilsBackEnd.getPdfStreamUrl();
    const headers = CoverpathUtilsBackEnd.getPdfStreamHeaders();

    const requestBody = {
      files: JSON.stringify(files),
    };

    const options = {
      url: PDFSTREAM_URL + '/merge/Agent',
      json: true,
      body: requestBody,
      method: 'POST',
      headers: headers,
    };

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathUtilsBackEnd',
        function: 'mergePDFs',
        data: {
          url: options.url,
        },
        category: UICategory.request,
      },
      `Merging pdfs`
    );

    return rp(options);
  }

  public static async checkSociety1851Qualification(primaryPolicy: CoverpathPolicy): Promise<boolean> {
    // primary advisor has fullCommission
    const fullCommission = !primaryPolicy.primaryPolicyAgent.ratio;

    const policyAgents = fullCommission
      ? [primaryPolicy.primaryPolicyAgent]
      : [primaryPolicy.primaryPolicyAgent, ...primaryPolicy.additionalPolicyAgents];
    const producerIds = policyAgents.map((p) => p.agent.producerId);
    const cdmResults = await new CdmService().getProducerDesignationsBulk(...producerIds);

    if (fullCommission) {
      return cdmResults[policyAgents[0].agent.producerId].blueChipCircle;
    }

    const totalRatio = policyAgents
      .filter((policyAgent) => cdmResults[policyAgent.agent.producerId].blueChipCircle)
      .reduce((sum, policyAgent) => sum + policyAgent.ratio, 0);

    return totalRatio >= SOCIETY_1851_THRESHOLD;
  }

  static getTimeInPrettyFormat(timeZone?: string) {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'long',
      timeZone: timeZone || 'UTC',
    });
    return formatter.format(date);
  }

  static formatPrettyDateTime(rawDate: Date): string {
    if (!rawDate) {
      return '';
    }

    const formatter = new Intl.DateTimeFormat('en', {
      hour: 'numeric',
      minute: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return formatter.format(rawDate);
  }

  static async handlePCHUpdates(
    property: PolicyAmendmentProperty,
    newValue,
    policy: CoverpathPolicy,
    editor: POLICY_CHANGE_EDITOR,
    txn: any
  ) {
    let existingPCH: PolicyChangeHistory;

    if (policy.policyChanges) {
      const existingPCHs = policy.policyChanges.filter(
        (item) => item.status === POLICY_CHANGE_HISTORY_STATUS.PENDING && item.property === property
      );

      if (existingPCHs.length > 1) {
        throw Error(
          `Cannot save amendments for policy ${policy.policyNumber}; already has more than one pending policy change history entity for ${property}`
        );
      }

      existingPCH = existingPCHs[0];
    }

    let valueOnPolicy, valueType;

    switch (property) {
      case PolicyAmendmentProperty.WAIVER:
      case PolicyAmendmentProperty.WHOLE_APL:
        valueOnPolicy = policy[property];
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.PAYMENT_FREQUENCY:
        valueOnPolicy = policy[property].toLowerCase();
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.WAIVER_AMOUNT:
        valueOnPolicy = policy.premiumBreakdown.waiver;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.BASE:
        valueOnPolicy = policy.premiumBreakdown.base;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.FLAT_EXTRA_JSON: {
        valueOnPolicy = jsonStringifyDeterministic(
          CoverpathUtilsBackEnd.getFlatExtraChangeList(policy.premiumBreakdown.flatExtras)
        );
        const newValueArray: Array<FlatExtra> = [];
        newValue.forEach((feUwObj) => {
          newValueArray.push(
            FlatExtra.new(
              feUwObj.name,
              Number(feUwObj.amount),
              null,
              null,
              feUwObj.ruleCode,
              Number(feUwObj.premium),
              feUwObj.expiryAfterInYears
            )
          );
        });
        newValue = jsonStringifyDeterministic(CoverpathUtilsBackEnd.getFlatExtraChangeList(newValueArray));
        valueType = ValueType.json;
        break;
      }
      case PolicyAmendmentProperty.TYPE:
        valueOnPolicy = policy.type;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.PRODUCT:
        valueOnPolicy = policy.product;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.PRODUCT_TYPE:
        valueOnPolicy = policy.productType;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.HAS_GIR_RIDER:
        valueOnPolicy = policy.hasGirRider;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.HAS_RTR_RIDER:
        valueOnPolicy = policy.hasRtrRider;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.HAS_ALIR_RIDER:
        valueOnPolicy = policy.hasAlirRider;
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.HAS_CIR_RIDER:
        valueOnPolicy = policy.hasCirRider;
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.RTR_PREMIUM_WAIVER:
        valueOnPolicy = policy.rtrRider.premiumWaiver;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.GIR_PREMIUM_WAIVER:
        valueOnPolicy = policy.girRider.premiumWaiver;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.RTR_PREMIUM_BASE:
        valueOnPolicy = policy.rtrRider.premiumBase;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.GIR_PREMIUM_BASE:
        valueOnPolicy = policy.girRider.premiumBase;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_PREMIUM_BASE:
        valueOnPolicy = policy.alirRider.premiumBase;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.RTR_AMOUNT:
        valueOnPolicy = policy.rtrRider.amount;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.GIR_AMOUNT:
        valueOnPolicy = policy.girRider.amount;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_AMOUNT:
        valueOnPolicy = policy.alirRider.amount;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_PLANNED_YEARS:
        valueOnPolicy = policy.alirRider.plannedYears;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_PAYMENT_FREQUENCY:
        valueOnPolicy = policy.alirRider.paymentFrequency;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.ALIR_1035_EXCHANGE_AMOUNT:
        valueOnPolicy = policy.alirRider.exchange1035.cashValueTransfer;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.EXISTING_POLICY_REPLACEMENT_CASH_VALUE_TRANSFER:
        // update the existing (replacement) policy that has cash value transfer
        const existingPolicyToModify: ExistingPolicy = policy.insured.existingPolicies.getCashValueReplacement();
        if (existingPolicyToModify) {
          valueOnPolicy = existingPolicyToModify.cashValueTransfer;
        } else {
          valueOnPolicy = null;
        }
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_1035_COST_BASIS:
        valueOnPolicy = policy.alirRider.exchange1035.costBasis;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_1035_POLICY_TYPE:
        valueOnPolicy = policy.alirRider.exchange1035.policyType;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.ALIR_1035_APPLY_TOWARDS:
        valueOnPolicy = policy.alirRider.exchange1035.applyTowards;
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.ALIR_1035_EXPECTED_ARRIVAL:
        valueOnPolicy = policy.alirRider.exchange1035.expectedArrival;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.ALIR_1035_IS_MEC:
        valueOnPolicy = policy.alirRider.exchange1035.isMec;
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.ALIR_1035_FUNDS_RECEIVED_DATE:
        valueOnPolicy = policy.alirRider.exchange1035.dateFundsReceived;
        valueType = ValueType.date;
        break;
      case PolicyAmendmentProperty.CIR_AMOUNT:
        valueOnPolicy = policy.cirRider.amount;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.CIR_PREMIUM_BASE:
        valueOnPolicy = policy.cirRider.premiumBase;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.CIR_PREMIUM_WAIVER:
        valueOnPolicy = policy.cirRider.premiumWaiver;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.CIR_BENEFIT_DISBURSEMENT_METHOD:
        valueOnPolicy = policy.cirRider.benefitDisbursementMethod;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.CIR_MONTHLY_INCREASE_OPTION:
        valueOnPolicy = policy.cirRider.monthlyIncreaseOption;
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.CIR_PREDICTED_BENEFIT_AGE:
        valueOnPolicy = policy.cirRider.predictedBenefitAge;
        valueType = ValueType.number;
        break;
      case PolicyAmendmentProperty.CIR_PUA_ADDED_TO_BENEFIT_POOL:
        valueOnPolicy = policy.cirRider.puaAddedToBenefitPool;
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.RTR_FLAT_EXTRAS:
        valueOnPolicy = jsonStringifyDeterministic(
          CoverpathUtilsBackEnd.getFlatExtraChangeList(policy.rtrRider.flatExtras)
        );
        const newValueArray: Array<FlatExtra> = [];
        newValue.forEach((feUwObj) => {
          newValueArray.push(
            FlatExtra.new(
              feUwObj.name,
              Number(feUwObj.amount),
              null,
              null,
              feUwObj.ruleCode,
              Number(feUwObj.premium),
              feUwObj.expiryAfterInYears
            )
          );
        });
        newValue = jsonStringifyDeterministic(CoverpathUtilsBackEnd.getFlatExtraChangeList(newValueArray));
        valueType = ValueType.json;
        break;
      case PolicyAmendmentProperty.HAS_ECP:
        valueOnPolicy = policy[property];
        valueType = ValueType.boolean;
        break;
      case PolicyAmendmentProperty.EFFECTIVE_DATE:
        valueOnPolicy = policy[property];
        valueType = ValueType.date;
        break;
      case PolicyAmendmentProperty.DIVIDEND_OPTION:
      case PolicyAmendmentProperty.LOAN_INTEREST_TYPE:
        valueOnPolicy = policy[property];
        valueType = ValueType.string;
        break;
      case PolicyAmendmentProperty.PRODUCT_PREMIUM_VERSION:
        valueOnPolicy = policy[property];
        valueType = ValueType.string;
        break;
      default:
        valueOnPolicy = policy[property];
        valueType = ValueType.number;
        break;
    }

    let valueIsNotSameInPCH = false;
    let valueIsSameOnPolicy = false;
    if (valueType === ValueType.date) {
      const aDateIsTruthy = !!valueOnPolicy;
      const bDateIsTruthy = !!newValue;
      const cDateIsTruthy = existingPCH && !!existingPCH.newValue;

      if (aDateIsTruthy && bDateIsTruthy) {
        valueIsSameOnPolicy = Utils.daysBetweenIgnoreTime(valueOnPolicy, newValue) === 0;
      } else {
        valueIsSameOnPolicy = !aDateIsTruthy && !bDateIsTruthy;
      }

      if (bDateIsTruthy && cDateIsTruthy) {
        valueIsNotSameInPCH = Utils.daysBetweenIgnoreTime(newValue, new Date(existingPCH.newValue)) > 0;
      } else {
        valueIsNotSameInPCH = !bDateIsTruthy && !cDateIsTruthy;
      }
    } else {
      valueIsSameOnPolicy = newValue === valueOnPolicy;
      valueIsNotSameInPCH = !!existingPCH && String(newValue) !== existingPCH.newValue;
    }

    //if we've previously edited face/term/etc., but no longer need it.... delete the amendment record
    if (!!existingPCH && valueIsSameOnPolicy) {
      policy.policyChanges = policy.policyChanges.filter(
        (item) => item.property !== property && item.status === POLICY_CHANGE_HISTORY_STATUS.PENDING
      );
      //We could avoid this explicit delete if we set "pruneOrphans": true on schema.json, but we are avoiding that
      //change to de-risk the possibility of breaking Haven amendment functionality.
      existingPCH.persistDelete({ transaction: txn });
    }
    //if we've previously edited face/term/etc... make sure the associated PCH has the most recent value
    else if (!!existingPCH && valueIsNotSameInPCH) {
      existingPCH.newValue = String(newValue);
      existingPCH.amendedDate = new Date();

      //There could auto amendments created before for the same property.
      //If advisor updates the same property change the editor to Advisor
      existingPCH.editor = POLICY_CHANGE_EDITOR.ADVISOR;

      existingPCH.setDirty(txn);
    }
    //this is the first time we are modifying, so add a PCH
    else if (!existingPCH && !valueIsSameOnPolicy) {
      let details: string = `Changing offer ${property}`;

      const legacyWLProductCodes = [
        Product.productTypes.Whole.productCodes.CPL10,
        Product.productTypes.Whole.productCodes.CPL12,
        Product.productTypes.Whole.productCodes.CPL15,
        Product.productTypes.Whole.productCodes.CPL20,
        Product.productTypes.Whole.productCodes.CPL65,
        Product.productTypes.Whole.productCodes.CPL100,
      ];

      const new7702WLCodes = [
        Product.productTypes.Whole.productCodes.CPL10A,
        Product.productTypes.Whole.productCodes.CPL15A,
        Product.productTypes.Whole.productCodes.CPL20A,
        Product.productTypes.Whole.productCodes.CPL65A,
        Product.productTypes.Whole.productCodes.CPL100A,
      ];

      if (legacyWLProductCodes.includes(newValue) && new7702WLCodes.includes(valueOnPolicy)) {
        details =
          details +
          `. This is allowed because an exception was given via launch darkly configuration for this policy to be amended from ${valueOnPolicy} to ${newValue}`;
      }

      const PCH = await PolicyChangeHistory.amendPolicy(
        policy,
        property,
        newValue,
        valueOnPolicy,
        valueType,
        POLICY_CHANGE_HISTORY_STATUS.PENDING,
        details,
        editor
      );
      policy.addPolicyChange(PCH);
    }
  }

  static getFlatExtraChangeList(flatExtras: Array<FlatExtra> | uwFlatExtras[]): CollectionItemChange[] {
    return flatExtras.map((fe) => {
      return {
        uniquePropertyName: 'ruleCode',
        uniquePropertyValue: fe.ruleCode,
        propBeingChanged: 'premium',
        newValue: fe.premium,
      };
    });
  }

  static async handlePCHTermUpdates(
    termAmendedValues: TermLifeAmendmentForm,
    policy: CoverpathPolicy,
    editor: POLICY_CHANGE_EDITOR,
    txn: any
  ) {
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.FACE_AMOUNT,
      Number(termAmendedValues.faceAmount),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.TERM,
      Number(termAmendedValues.coverageTerm),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(PolicyAmendmentProperty.WAIVER, termAmendedValues.waiverOfPremium, policy, editor, txn);
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.PAYMENT_FREQUENCY,
      termAmendedValues.paymentFrequency.toLowerCase(),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(PolicyAmendmentProperty.BASE, Number(termAmendedValues.premium), policy, editor, txn);
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.WAIVER_AMOUNT,
      Number(termAmendedValues.waiver),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.FLAT_EXTRA_JSON,
      termAmendedValues.flatExtras,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(PolicyAmendmentProperty.PRODUCT, termAmendedValues.product, policy, editor, txn);
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.PRODUCT_TYPE,
      termAmendedValues.productType,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(PolicyAmendmentProperty.HAS_ECP, termAmendedValues.ecp, policy, editor, txn);
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.EFFECTIVE_DATE,
      termAmendedValues.effectiveDate,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.PRODUCT_PREMIUM_VERSION,
      termAmendedValues.productPremiumVersion,
      policy,
      editor,
      txn
    );
  }

  static async handlePCHWholeUpdates(
    wholeLifeAmendedValues: WholeLifeAmendmentForm,
    policy: CoverpathPolicy,
    editor: POLICY_CHANGE_EDITOR,
    txn: any
  ) {
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.FACE_AMOUNT,
      Number(wholeLifeAmendedValues.faceAmount),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.TERM,
      Number(wholeLifeAmendedValues.coverageTerm),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.WAIVER,
      wholeLifeAmendedValues.waiverOfPremium,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.PAYMENT_FREQUENCY,
      wholeLifeAmendedValues.paymentFrequency.toLowerCase(),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.BASE,
      Number(wholeLifeAmendedValues.premium),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.WAIVER_AMOUNT,
      Number(wholeLifeAmendedValues.waiver),
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.FLAT_EXTRA_JSON,
      wholeLifeAmendedValues.flatExtras,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(PolicyAmendmentProperty.PRODUCT, wholeLifeAmendedValues.product, policy, editor, txn);
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.PRODUCT_TYPE,
      wholeLifeAmendedValues.productType,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.EFFECTIVE_DATE,
      wholeLifeAmendedValues.effectiveDate,
      policy,
      editor,
      txn
    );

    if (policy.girRider) {
      const hasGirRiderWithPendingAmendment = wholeLifeAmendedValues.riders.gir.enabled;
      await this.handlePCHUpdates(
        PolicyAmendmentProperty.HAS_GIR_RIDER,
        hasGirRiderWithPendingAmendment.toString(),
        policy,
        editor,
        txn
      );

      if (hasGirRiderWithPendingAmendment) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.GIR_PREMIUM_BASE,
          Number(wholeLifeAmendedValues.riders.gir.premium),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.GIR_PREMIUM_WAIVER,
          Number(wholeLifeAmendedValues.riders.gir.waiver),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.GIR_AMOUNT,
          Number(wholeLifeAmendedValues.riders.gir.amount),
          policy,
          editor,
          txn
        );
      } else {
        policy.policyChanges
          .filter(
            (pch) =>
              pch.property === PolicyAmendmentProperty.GIR_AMOUNT ||
              pch.property === PolicyAmendmentProperty.GIR_PREMIUM_BASE ||
              pch.property === PolicyAmendmentProperty.GIR_PREMIUM_WAIVER
          )
          .forEach(async (pch) => {
            await pch.persistDelete({ transaction: txn });
          });

        policy.policyChanges = policy.policyChanges.filter(
          (pch) =>
            pch.property !== PolicyAmendmentProperty.GIR_AMOUNT &&
            pch.property !== PolicyAmendmentProperty.GIR_PREMIUM_BASE &&
            pch.property !== PolicyAmendmentProperty.GIR_PREMIUM_WAIVER
        );
      }
    }

    if (policy.rtrRider) {
      const hasRtrRiderWithPendingAmendment = wholeLifeAmendedValues.riders.rtr.enabled;
      await this.handlePCHUpdates(
        PolicyAmendmentProperty.HAS_RTR_RIDER,
        hasRtrRiderWithPendingAmendment.toString(),
        policy,
        editor,
        txn
      );

      if (hasRtrRiderWithPendingAmendment) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.RTR_PREMIUM_BASE,
          Number(wholeLifeAmendedValues.riders.rtr.premium),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.RTR_PREMIUM_WAIVER,
          Number(wholeLifeAmendedValues.riders.rtr.waiver),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.RTR_AMOUNT,
          Number(wholeLifeAmendedValues.riders.rtr.amount),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.RTR_FLAT_EXTRAS,
          wholeLifeAmendedValues.riders.rtr.flatExtras,
          policy,
          editor,
          txn
        );
      } else {
        policy.policyChanges
          .filter(
            (pch) =>
              pch.property === PolicyAmendmentProperty.RTR_AMOUNT ||
              pch.property === PolicyAmendmentProperty.RTR_PREMIUM_BASE ||
              pch.property === PolicyAmendmentProperty.RTR_PREMIUM_WAIVER ||
              pch.property === PolicyAmendmentProperty.RTR_FLAT_EXTRAS
          )
          .forEach(async (pch) => {
            await pch.persistDelete({ transaction: txn });
          });

        policy.policyChanges = policy.policyChanges.filter(
          (pch) =>
            pch.property !== PolicyAmendmentProperty.RTR_AMOUNT &&
            pch.property !== PolicyAmendmentProperty.RTR_PREMIUM_BASE &&
            pch.property !== PolicyAmendmentProperty.RTR_PREMIUM_WAIVER &&
            pch.property !== PolicyAmendmentProperty.RTR_FLAT_EXTRAS
        );
      }
    }

    if (policy.alirRider) {
      const hasAlirRiderWithPendingAmendment = wholeLifeAmendedValues.riders.alir.enabled;
      const has1035ExchangeWithPendingAmendments =
        !!wholeLifeAmendedValues.exchange1035 && wholeLifeAmendedValues.exchange1035.enabled;
      await this.handlePCHUpdates(
        PolicyAmendmentProperty.HAS_ALIR_RIDER,
        hasAlirRiderWithPendingAmendment || has1035ExchangeWithPendingAmendments,
        policy,
        editor,
        txn
      );

      // if there is a 1035 and it is being removed, we have to set the cashValueTransfer
      // of the replacement policy
      if (policy.alirRider.exchange1035 && !has1035ExchangeWithPendingAmendments) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.EXISTING_POLICY_REPLACEMENT_CASH_VALUE_TRANSFER,
          0,
          policy,
          editor,
          txn
        );
      }

      if (hasAlirRiderWithPendingAmendment) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_PREMIUM_BASE,
          Number(wholeLifeAmendedValues.riders.alir.premium),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_AMOUNT,
          Number(wholeLifeAmendedValues.riders.alir.amount),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_PLANNED_YEARS,
          Number(wholeLifeAmendedValues.riders.alir.plannedYears),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_PAYMENT_FREQUENCY,
          wholeLifeAmendedValues.riders.alir.paymentFrequency.toLowerCase(),
          policy,
          editor,
          txn
        );
      } else if (has1035ExchangeWithPendingAmendments) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_EXCHANGE_AMOUNT,
          Number(wholeLifeAmendedValues.exchange1035.cashValueTransfer),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.EXISTING_POLICY_REPLACEMENT_CASH_VALUE_TRANSFER,
          Number(wholeLifeAmendedValues.exchange1035.cashValueTransfer),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_COST_BASIS,
          Number(wholeLifeAmendedValues.exchange1035.costBasis),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_POLICY_TYPE,
          wholeLifeAmendedValues.exchange1035.policyType,
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_APPLY_TOWARDS,
          wholeLifeAmendedValues.exchange1035.applyTowards,
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_EXPECTED_ARRIVAL,
          Number(wholeLifeAmendedValues.exchange1035.expectedArrival),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.ALIR_1035_IS_MEC,
          Boolean(wholeLifeAmendedValues.exchange1035.isMec),
          policy,
          editor,
          txn
        );
      } else {
        const changesToDelete: Array<PolicyChangeHistory> = policy.policyChanges.filter(
          (pch) =>
            pch.property === PolicyAmendmentProperty.ALIR_AMOUNT ||
            pch.property === PolicyAmendmentProperty.ALIR_PREMIUM_BASE ||
            pch.property === PolicyAmendmentProperty.ALIR_PLANNED_YEARS ||
            pch.property === PolicyAmendmentProperty.ALIR_PAYMENT_FREQUENCY ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_EXCHANGE_AMOUNT ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_COST_BASIS ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_POLICY_TYPE ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_APPLY_TOWARDS ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_EXPECTED_ARRIVAL ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_IS_MEC ||
            pch.property === PolicyAmendmentProperty.ALIR_1035_FUNDS_RECEIVED_DATE
        );
        await Promise.all(changesToDelete.map((pch) => pch.persistorDelete({ transaction: txn })));

        policy.policyChanges = policy.policyChanges.filter(
          (pch) =>
            pch.property !== PolicyAmendmentProperty.ALIR_AMOUNT &&
            pch.property !== PolicyAmendmentProperty.ALIR_PREMIUM_BASE &&
            pch.property !== PolicyAmendmentProperty.ALIR_PLANNED_YEARS &&
            pch.property !== PolicyAmendmentProperty.ALIR_PAYMENT_FREQUENCY &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_EXCHANGE_AMOUNT &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_COST_BASIS &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_POLICY_TYPE &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_APPLY_TOWARDS &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_EXPECTED_ARRIVAL &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_IS_MEC &&
            pch.property !== PolicyAmendmentProperty.ALIR_1035_FUNDS_RECEIVED_DATE
        );
      }
    }

    if (policy.cirRider) {
      const hasCirRiderWithPendingAmendment = wholeLifeAmendedValues.riders.cir.enabled;
      await this.handlePCHUpdates(
        PolicyAmendmentProperty.HAS_CIR_RIDER,
        Boolean(hasCirRiderWithPendingAmendment),
        policy,
        editor,
        txn
      );
      if (hasCirRiderWithPendingAmendment) {
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_AMOUNT,
          Number(wholeLifeAmendedValues.riders.cir.amount),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_PREMIUM_BASE,
          Number(wholeLifeAmendedValues.riders.cir.premium),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_PREMIUM_WAIVER,
          Number(wholeLifeAmendedValues.riders.cir.waiver),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_BENEFIT_DISBURSEMENT_METHOD,
          Number(wholeLifeAmendedValues.riders.cir.benefitDisbursementMethod),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_MONTHLY_INCREASE_OPTION,
          Boolean(wholeLifeAmendedValues.riders.cir.monthlyIncreaseOption),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_PREDICTED_BENEFIT_AGE,
          Number(wholeLifeAmendedValues.riders.cir.predictedBenefitAge),
          policy,
          editor,
          txn
        );
        await this.handlePCHUpdates(
          PolicyAmendmentProperty.CIR_PUA_ADDED_TO_BENEFIT_POOL,
          Boolean(wholeLifeAmendedValues.riders.cir.puaAddedToBenefitPool),
          policy,
          editor,
          txn
        );
      } else {
        policy.policyChanges
          .filter(
            (pch) =>
              pch.property === PolicyAmendmentProperty.CIR_AMOUNT ||
              pch.property === PolicyAmendmentProperty.CIR_PREMIUM_BASE ||
              pch.property === PolicyAmendmentProperty.CIR_PREMIUM_WAIVER ||
              pch.property === PolicyAmendmentProperty.CIR_BENEFIT_DISBURSEMENT_METHOD ||
              pch.property === PolicyAmendmentProperty.CIR_MONTHLY_INCREASE_OPTION ||
              pch.property === PolicyAmendmentProperty.CIR_PREDICTED_BENEFIT_AGE ||
              pch.property === PolicyAmendmentProperty.CIR_PUA_ADDED_TO_BENEFIT_POOL
          )
          .forEach(async (pch) => {
            await pch.persistDelete({ transaction: txn });
          });

        policy.policyChanges = policy.policyChanges.filter(
          (pch) =>
            pch.property !== PolicyAmendmentProperty.CIR_AMOUNT &&
            pch.property !== PolicyAmendmentProperty.CIR_PREMIUM_BASE &&
            pch.property !== PolicyAmendmentProperty.CIR_PREMIUM_WAIVER &&
            pch.property !== PolicyAmendmentProperty.CIR_BENEFIT_DISBURSEMENT_METHOD &&
            pch.property !== PolicyAmendmentProperty.CIR_MONTHLY_INCREASE_OPTION &&
            pch.property !== PolicyAmendmentProperty.CIR_PREDICTED_BENEFIT_AGE &&
            pch.property !== PolicyAmendmentProperty.CIR_PUA_ADDED_TO_BENEFIT_POOL
        );
      }
    }

    await this.handlePCHUpdates(
      PolicyAmendmentProperty.LOAN_INTEREST_TYPE,
      wholeLifeAmendedValues.loanType,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.DIVIDEND_OPTION,
      wholeLifeAmendedValues.dividendOption,
      policy,
      editor,
      txn
    );
    await this.handlePCHUpdates(
      PolicyAmendmentProperty.WHOLE_APL,
      wholeLifeAmendedValues.wholeAPL,
      policy,
      editor,
      txn
    );
  }

  static policyToElasticPayload(annuity: CoverpathAnnuity) {
    let party = {};
    let id = annuity.policyNumber;
    if (!id || id.length === 0) {
      id = (annuity.application as CoverpathAnnuityApplication)._id;
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathUtilsBackEnd',
        function: 'policyToElasticPayload',
        data: {
          id,
        },
        category: UICategory.request,
      },
      `Generating elastic payload`
    );
    const owner = annuity.owner;
    let emailSearch = owner.email;
    if (owner.email) {
      // Remove all characters that are not a letter or number
      emailSearch = owner.email.replace(/[^a-zA-Z0-9]/g, '');
    }
    const address = getDefaultAddressForParty(owner);

    if (owner instanceof Person) {
      let ssn = owner.getSsn(false);
      if (ssn.length !== 9) {
        // Elastic will reject any user with an SSN thats not 9 digits, so use a placeholder instead
        ssn = '000000000';
      }

      party = {
        firstName: owner.firstName,
        lastName: owner.lastName,
        middleName: owner.middleName,
        name: owner.getFullName(),
        email: owner.email,
        dob: owner.dob,
        emailSearch,
        ssn: ssn,
        ssnLastFour: ssn.slice(-4),
        govtId: ssn,
        state: address ? address.state : null,
        role: 'Owner/Insured',
      };
    } else {
      party = {
        name: annuity.ownerEntity.getFullNameOfOwner(),
        govtId: annuity.ownerEntity.taxId || '',
        state: annuity.ownerEntity.trustSitus || '',
        email: owner.email,
        emailSearch: emailSearch,
        dob: null,
        firstName: null,
        middleName: null,
        lastName: null,
        ssn: 'None',
      };
    }

    const agent = annuity.primaryPolicyAgent.agent;
    const advisor = {
      name: (agent.party as Person).getFullName(),
      advisorId: agent.producerId,
      advisorSSN: '',
      email: agent.party.email,
      phone: agent.party.phone.number,
      agentAssisted: annuity.application.isAgentAssistant,
    };
    const payload = {
      eventType: 'summary',
      id,
      customerId: annuity.owner._id,
      channel: annuity.getChannelCode(),
      policy: annuity.policyNumber,
      policyLastFour: annuity.policyNumber ? annuity.policyNumber.slice(-4) : null,
      covered: null,
      otherOwner: null,
      submitted: annuity.submittedAt,
      ownerClassification: annuity.ownerClassification,
      trustOrEntity: annuity.trustOrOtherEntity ? annuity.trustOrOtherEntity.type : null,
      applicationId: annuity.application._id,
      juvieSigner: null,
      product: annuity.product,
      advisor,
      ...party,
    };

    if (annuity.otherOwner instanceof Person) {
      const otherOwner = {
        firstName: annuity.otherOwner.firstName,
        lastName: annuity.otherOwner.lastName,
        middleName: annuity.otherOwner.middleName,
        ssn: annuity.otherOwner.SSN.replace(/-/gi, ''),
        email: annuity.otherOwner.email,
        emailSearch: annuity.otherOwner.email.replace(/[^a-zA-Z0-9]/g, ''),
        role: 'OtherOwner',
        state: annuity.otherOwner.residentialAddress.state,
        dob: annuity.otherOwner.dob,
        channel: annuity.customer.channel,
      };

      payload.otherOwner = otherOwner;
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathUtilsBackEnd',
        function: 'policyToElasticPayload',
        context: {
          piiLevel: UIPiiLevelEnum.s1,
        },
        data: {
          payload,
        },
        category: UICategory.request,
      },
      `Elastic payload generated`
    );

    return payload;
  }

  static elasticPutAnnuity(annuity: CoverpathAnnuity) {
    const elasticPayload = CoverpathUtilsBackEnd.policyToElasticPayload(annuity);
    RabbitmqPublisher.singleton().publish(elasticPayload, ElasticSchema);
  }

  static generateCorpAgencyString(agent: Agent) {
    const defaultAgencyProps: AgencyCorpsRelationship = {
      agencyBpid: agent.agencyId || null,
      corpsBpid: agent.corpId || null,
      agencyName: agent.agencyName || null,
      corpsName: null,
    };
    return JSON.stringify(defaultAgencyProps);
  }

  static isSuitabilityFormNeeded(new_plan: AnnuityMarket): boolean {
    if (
      new_plan === AnnuityMarket._401K ||
      new_plan === AnnuityMarket.KEOGH ||
      new_plan === AnnuityMarket.MONEY_PURCHASE_PENSION_PLAN ||
      new_plan === AnnuityMarket.PENSION_PLAN ||
      new_plan === AnnuityMarket.PROFIT_SHARING_PLAN
    ) {
      // FR3013 required
      return false;
    } else {
      // suitability required
      return true;
    }
  }

  static isEligibleForWholeLifeConversion(termPolicy: CoverpathPolicy, wholePolicy: CoverpathPolicy) {
    return (
      wholePolicy.isWholeLifePolicy() &&
      wholePolicy.isInForce() &&
      EligibleToConvertToExistingWLProductTypes.includes(wholePolicy.product) &&
      wholePolicy.faceAmount >= LowFaceWholeLife.maxFace
    );
  }

  static async hasSameEntities(termPolicy: CoverpathPolicy, wholePolicy: CoverpathPolicy): Promise<boolean> {
    let hasSameInsured = false,
      hasSameOwner = false;

    if (
      termPolicy.application.applicationNumber &&
      wholePolicy.application.applicationNumber &&
      termPolicy.application.applicationNumber === wholePolicy.application.applicationNumber
    ) {
      hasSameOwner = true; //is a joint app
    } else if (wholePolicy.ownerEntity && termPolicy.ownerEntity && wholePolicy.ownerEntity.taxId) {
      hasSameOwner = wholePolicy.ownerEntity.taxId === termPolicy.ownerEntity.taxId;
    } else if (wholePolicy.ownerPerson && termPolicy.ownerPerson) {
      const wholePolicySSNPii = wholePolicy.ownerPerson.getSsn(true);
      if (wholePolicySSNPii) {
        hasSameOwner = await SecurityUtils.isSSNSame(wholePolicySSNPii, termPolicy.ownerPerson.getSsn(true));
      }
    }

    //Joint ownership
    if (termPolicy.isJointOwnership()) {
      if (wholePolicy.isJointOwnership() && wholePolicy.otherOwner && termPolicy.otherOwner) {
        const wholePolicySSNPii = wholePolicy.otherOwner.getSsn(true);
        if (wholePolicySSNPii) {
          hasSameOwner = await SecurityUtils.isSSNSame(wholePolicySSNPii, termPolicy.otherOwner.getSsn(true));
        }
      } else {
        hasSameOwner = false;
      }
    }

    if (wholePolicy.insured.person && termPolicy.insured.person) {
      const wholePolicySSNPii = wholePolicy.insured.person.getSsn(true);
      if (wholePolicySSNPii) {
        hasSameInsured = await SecurityUtils.isSSNSame(wholePolicySSNPii, termPolicy.insured.person.getSsn(true));
      }
    }
    return hasSameInsured && hasSameOwner;
  }
}
